import React from 'react';
import Link from 'next/link';
import {
  CalendarDays,
  User,
  MapPin,
  Trash2,
  ThumbsUp,
  ThumbsDown,
  ArrowRight,
} from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Loader } from '../../ui/loader';

import CustomContainer from '../../common/CustomContainer';
import { formatDatetime } from '../../Utils';

interface ArticleProps {
  article: any;
  isMobile: boolean;
  showAuthor?: boolean;
  showBoard?: boolean;
  showPushStats?: boolean;
}

export const Article = ({
  article,
  isMobile,
  showAuthor,
  showBoard,
  showPushStats,
}: ArticleProps) => (
  <Link href={`/ptt/article/${article.id}`} className="block">
    <div className="p-4 hover:bg-gray-50 transition-colors border-b last:border-b-0">
      <div className="flex flex-col">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-lg flex-1">{article.title}</h3>
          {showBoard && (
            <Badge
              variant="default"
              className="bg-teal-600 hover:bg-teal-700 ml-2"
            >
              {article.board.alias ?? article.board.title} (
              {article.board.title})
            </Badge>
          )}
        </div>
        <div className="flex items-end justify-between">
          <div className="space-y-1 text-sm text-gray-600 flex-1">
            <div className="flex items-center gap-1">
              <CalendarDays className="h-4 w-4" />
              {formatDatetime(article.datetime)}
            </div>
            {showAuthor && (
              <div className="flex items-center gap-1">
                <User className="h-4 w-4" />
                {article.author_id}
              </div>
            )}
            {article.code2 && (
              <div className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {article.city} ({article.country}{' '}
                <span className={`fi fi-${article.code2.toLowerCase()}`}></span>
                )
              </div>
            )}
            {!article.active && (
              <div className="flex items-center gap-1 text-red-600">
                <Trash2 className="h-4 w-4" />
                <span>此文章已被刪除囉!!</span>
              </div>
            )}
          </div>
          {showPushStats && (
            <div className="flex items-center gap-3 text-sm ml-4">
              <div className="flex items-center gap-1">
                <ThumbsUp className="h-4 w-4 text-blue-600" />
                <span>{article.num_likes}</span>
              </div>
              <div className="flex items-center gap-1">
                <ThumbsDown className="h-4 w-4 text-red-600" />
                <span>{article.num_hates}</span>
              </div>
              <div className="flex items-center gap-1">
                <ArrowRight className="h-4 w-4 text-yellow-600" />
                <span>{article.num_replies}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </Link>
);

interface ArticlesProps {
  data: any[];
  fetchFunc: (page: number) => void;
  isFetching: boolean;
  isMobile: boolean;
  offset: number;
  pageSize: number;
  showAuthor?: boolean;
  showBoard?: boolean;
  showPushStats?: boolean;
  title?: string;
  total: number;
}

const Articles: React.FC<ArticlesProps> = ({
  data,
  fetchFunc,
  isFetching,
  isMobile,
  offset,
  pageSize,
  showAuthor = false,
  showBoard = false,
  showPushStats = false,
  title,
  total,
}) => {
  const onPageChange = (page: number) => {
    fetchFunc(page);
  };

  const n = data.length;

  if (n === 0 && !isFetching) {
    return null;
  }

  const currentPage = Math.floor(offset / pageSize) + 1;

  return (
    <CustomContainer>
      <Card>
        <CardHeader>
          <CardTitle className={isMobile ? 'text-xl' : 'text-2xl'}>
            {title || '所有文章'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isFetching ? (
            <div className="flex justify-center py-8">
              <Loader />
            </div>
          ) : (
            <>
              <div className="divide-y">
                {data.map(article => (
                  <Article
                    key={article.id}
                    article={article}
                    isMobile={isMobile}
                    showAuthor={showAuthor}
                    showBoard={showBoard}
                    showPushStats={showPushStats}
                  />
                ))}
              </div>
              {total > 1 && (
                <div className="flex justify-center items-center gap-1 p-4 flex-wrap">
                  {/* First page */}
                  <Button
                    variant={currentPage === 1 ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => onPageChange(1)}
                    disabled={isFetching}
                  >
                    1
                  </Button>

                  {/* Show ellipsis if there's a gap */}
                  {currentPage > 3 && <span className="px-2">...</span>}

                  {/* Pages around current page */}
                  {Array.from({ length: total }, (_, i) => i + 1)
                    .filter(page => {
                      if (page === 1 || page === total) return false;
                      if (isMobile) {
                        return Math.abs(page - currentPage) <= 1;
                      }
                      return Math.abs(page - currentPage) <= 2;
                    })
                    .map(page => (
                      <Button
                        key={page}
                        variant={currentPage === page ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => onPageChange(page)}
                        disabled={isFetching}
                      >
                        {page}
                      </Button>
                    ))}

                  {/* Show ellipsis if there's a gap */}
                  {currentPage < total - 2 && <span className="px-2">...</span>}

                  {/* Last page */}
                  {total > 1 && (
                    <Button
                      variant={currentPage === total ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => onPageChange(total)}
                      disabled={isFetching}
                    >
                      {total}
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </CustomContainer>
  );
};

export default Articles;

